/**
 * useRibbonTabState — Track 3 Phase 7
 *
 * Owns the ribbon-tab-bar selection extracted from `App.tsx` per
 * `docs/plans/2026-05-08_track3_app_decomposition.md` row 7
 * ("Cleanup: ribbon / context menu / inspector / coord-system").
 *
 * Responsibilities:
 *   - Track which ribbon tab id is currently active (default "draw").
 *   - Auto-switch the active tab when the editor crosses the BEDIT
 *     boundary (Phase J.3): entering block-edit forces the
 *     "block-editor" tab, leaving block-edit restores the "draw" tab
 *     so a previously-selected tab like "draw" doesn't render an
 *     empty panel after the visible-tabs list narrows.
 *
 * Returned shape:
 *   { activeRibbonTab, setActiveRibbonTab }
 *
 * AutoCAD parity:
 *   AutoCAD's ribbon swaps to the "Block Editor" contextual tab
 *   group automatically when BEDIT starts and reverts when BCLOSE
 *   exits. The auto-switch effect mirrors that contextual ribbon
 *   behaviour.
 */

import { useEffect, useState } from "react";

import type { RibbonTabId } from "../components/RibbonConfig";

export interface UseRibbonTabStateParams {
  /** True while the editor is inside BEDIT (block-edit space active). */
  isInBlockEdit: boolean;
}

export interface UseRibbonTabStateResult {
  activeRibbonTab: RibbonTabId;
  setActiveRibbonTab: React.Dispatch<React.SetStateAction<RibbonTabId>>;
}

export function useRibbonTabState({
  isInBlockEdit,
}: UseRibbonTabStateParams): UseRibbonTabStateResult {
  const [activeRibbonTab, setActiveRibbonTab] = useState<RibbonTabId>("draw");

  // Phase J.3 — auto-switch active ribbon tab when crossing the BEDIT
  // boundary. Entering BEDIT swaps `ribbonTabIds` for
  // `blockEditorRibbonTabIds`, so a previously-active tab like "draw"
  // would render an empty panel until the user manually re-clicks.
  // Reset to the first tab of the new mode whenever BEDIT toggles.
  useEffect(() => {
    if (isInBlockEdit) {
      setActiveRibbonTab("block-editor");
    } else if (activeRibbonTab === "block-editor") {
      // Coming back from BEDIT — restore the default "draw" tab.
      setActiveRibbonTab("draw");
    }
    // We intentionally exclude `activeRibbonTab` from deps: this
    // effect only fires on the BEDIT boundary, not on every tab
    // click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInBlockEdit]);

  return { activeRibbonTab, setActiveRibbonTab };
}
